import React, { useMemo, useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { diff } from "json8-patch";
import { FormikHelpers } from "formik";

import {
  SectionMenuItemsModel,
  SectionDynamicMenuItemsPages,
} from "../../../sewer-objects/section-details/SectionMenuItems";
import { EditDamage, EditDamageFromAssignedProject } from "../../../../services/authorization/Permissions";
import { EditDynamicProperties } from "../../../../components/data-components/EditDynamicProperties";
import PageLayout from "../../../_layout/PageLayout";
import Form from "../../../../components/form/Form";
import { CurrentSectionStorageModel } from "../../../../services/sewer-object/CurrentSectionStorage";
import { CurrentDamageStorageModel } from "../../../../services/damage/CurrentDamageStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { EditDamageImage, DamageFormValues } from "../../components/DataComponents";
import {
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import { DamagePropertiesStorageModel } from "../../../../services/damage/DamagePropertiesStorage";
import EditGroupCode from "../../components/EditGroupCode";
import { DamageGroupCode } from "../../../../services/damage/DamageGroupCode";
import EditMainCode from "../../components/EditMainCode";
import { DamageMainCode } from "../../../../services/damage/DamageMainCode";
import EditC1Code from "../../components/EditC1Code";
import EditC2Code from "../../components/EditC2Code";
import { DamageC1Code } from "../../../../services/damage/DamageC1Code";
import { DamageC2Code } from "../../../../services/damage/DamageC2Code";
import { UploadImageStateModel } from "../../../../components/image-upload/ImageUploadModel";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { CurrentInspectionStorageModel } from "../../../../services/inspection/CurrentInspectionStorage";
import { DamageCodesStorageModel } from "../../../../services/damage/DamageCodesStorage";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../../helpers/StringHelpers";
import useRouteService from "../../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { FieldsFromBM } from "../../../../api-models/response/BluemetricFields";

const EditDamagePage = observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [currentDamageStorage] = useState(() =>
    CurrentDamageStorageModel.create(
      { currentDamageId: urlParamsService.getDamageId()!, autoLoad: false },
      { api }
    )
  );

  const [damagePropertiesStorageModel] = useState(() =>
    DamagePropertiesStorageModel.create(
      { sewerObjectId: urlParamsService.getSectionId()! },
      { api }
    )
  );

  const [damageCodesStorage] = useState(DamageCodesStorageModel.create());

  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const [imageUploadState] = useState(() => UploadImageStateModel.create({}));

  useEffect(() => {
    const setupDamageDataForEdit = async () => {
      try {
        await currentDamageStorage.load();
        await damagePropertiesStorageModel.getDamageGroupCodes();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.edit-setup");
        handleErrorInSnackbar(applicationError);
      }

      damageCodesStorage.initialDataReceived(
        damagePropertiesStorageModel.groupCodes,
        currentDamageStorage.currentDamage
      );

      await getDamageDetails();
    };
    setupDamageDataForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      inspectionId: currentInspectionId,
      damageId: currentDamageStorage.currentDamage.id,
      dynamicMenuItemPage: SectionDynamicMenuItemsPages.EditDamage,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentDamageStorage.currentDamage.id,
    currentInspectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.currentSectionId,
    currentSectionStorage.parentEntityFromPath.id,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const isPageLoading = useCallback(
    () => currentDamageStorage.isLoading || damagePropertiesStorageModel.areGroupCodesLoading,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const damageIsLockedWarning = useMemo(() => {
    if (
      currentDamageStorage.userWithLockOnDamage &&
      !currentDamageStorage.currentUserHasLockOnDamage
    )
      return {
        key: "EditDamage.locked-inspection",
        context: { userName: currentDamageStorage.userWithLockOnDamage.name },
      };
    else return undefined;
  }, [currentDamageStorage.userWithLockOnDamage, currentDamageStorage.currentUserHasLockOnDamage]);

  const getDynamicPropertiesInitialValues = useCallback(() => {
    if (damageCodesStorage.damageCharacteristicsProperties)
      return damageCodesStorage.extractFieldsFromBM(
        damageCodesStorage.damageCharacteristicsProperties
      );
  }, [damageCodesStorage]);

  const getInitialFormValues = useCallback(() => {
    return {
      dynamicProperties: getDynamicPropertiesInitialValues(),
      groupCode: "all",
      mainCode: damageCodesStorage.initialMainCode,
      c1Code: damageCodesStorage.initialC1Code,
      c2Code: damageCodesStorage.initialC2Code,
    };
  }, [
    damageCodesStorage.initialC1Code,
    damageCodesStorage.initialC2Code,
    damageCodesStorage.initialMainCode,
    getDynamicPropertiesInitialValues,
  ]);

  const goToDamagePage = useCallback(
    () => history.push(`${navigatePath(history.location.pathname, -1)}`),
    [history]
  );

  const isLoadingDamageCharacteristics = useCallback(() => {
    return damageCodesStorage.loadingDamageCharacteristics;
  }, [damageCodesStorage.loadingDamageCharacteristics]);

  const updateDamageDetails = useCallback(
    async (updatedFormValues: FieldsFromBM) => {
      const oldMainCode = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.mainCodeFieldIndicator,
        damageCodesStorage.oldMainCodeValue
      );
      const oldC1Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c1CodeFieldIndicator,
        damageCodesStorage.oldC1CodeValue
      );
      const oldC2Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c2CodeFieldIndicator,
        damageCodesStorage.oldC2CodeValue
      );
      const oldDamageCharacteristics = damageCodesStorage.extractFieldsFromBM(
        damageCodesStorage.oldDamageCharacteristicsProperties
      );

      const oldValuesFlattened = {
        ...oldDamageCharacteristics,
        ...oldMainCode,
        ...oldC1Code,
        ...oldC2Code,
      };
      const newCleanedFormatValues =
        currentDamageStorage.currentDamage.getDynamicPropertiesWithDecimalsAndDateFormatted(
          updatedFormValues
        );
      let patchJson = diff(oldValuesFlattened, newCleanedFormatValues);
      await currentDamageStorage.editDamageDetails(patchJson);
    },
    [damageCodesStorage, currentDamageStorage]
  );

  const updateDamageImage = useCallback(async () => {
    if (imageUploadState.shouldDeleteCurrentImage)
      return await currentDamageStorage.deleteDamageImage();
    if (imageUploadState.imageForUpload)
      return await currentDamageStorage.editDamageImage(imageUploadState.imageForUpload);
  }, [
    currentDamageStorage,
    imageUploadState.imageForUpload,
    imageUploadState.shouldDeleteCurrentImage,
  ]);

  const getDamageDetails = useCallback(async () => {
    try {
      damageCodesStorage.setLoadingDamageCharacteristics();
      const c1CodeParam = !damageCodesStorage.selectedMainCode?.hasC1Codes
        ? ""
        : damageCodesStorage.selectedC1Code.code;
      await damagePropertiesStorageModel.getDamageDetailsForCodes(
        damageCodesStorage.selectedMainCode?.code ?? "",
        c1CodeParam,
        damageCodesStorage.selectedC2Code.code
      );
      damageCodesStorage.receivedDamageCharacteristics(
        damagePropertiesStorageModel.damageDetailsForCodes
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.damage.characteristics");
      handleErrorInSnackbar(applicationError);
    }
  }, [damageCodesStorage, damagePropertiesStorageModel, handleErrorInSnackbar]);

  const handleSubmit = useCallback(
    async (formValues: DamageFormValues, formActions: FormikHelpers<FieldsFromBM>) => {
      const mainCode = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.mainCodeFieldIndicator,
        damageCodesStorage.selectedMainCode?.code
      );
      const c1Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c1CodeFieldIndicator,
        !damageCodesStorage.selectedMainCode?.hasC1Codes
          ? ""
          : damageCodesStorage.selectedC1Code.code
      );
      const c2Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c2CodeFieldIndicator,
        damageCodesStorage.selectedC2Code.code
      );

      const dynamicPropertiesToUpdate = {
        ...formValues.dynamicProperties,
        ...mainCode,
        ...c1Code,
        ...c2Code,
      };

      Object.keys(dynamicPropertiesToUpdate).forEach((key) => {
        if (
          damagePropertiesStorageModel.damageDetailsForCodes.disabledFields.find(
            (disabledField) => disabledField === key
          )
        ) {
          dynamicPropertiesToUpdate[key] = null;
        }
      });

      try {
        if (imageUploadState.shouldUpdateImage()) await updateDamageImage();

        await updateDamageDetails(dynamicPropertiesToUpdate);
        warningLeavePageService.setIsDirty(false);
        return goToDamagePage();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.edit");
        applicationError.withForm(dynamicPropertiesToUpdate, formActions);
        handleErrorInSnackbar(applicationError);
      }
    },
    [
      damageCodesStorage,
      damagePropertiesStorageModel.damageDetailsForCodes.disabledFields,
      imageUploadState,
      updateDamageImage,
      updateDamageDetails,
      goToDamagePage,
      handleErrorInSnackbar,
    ]
  );

  const getC2Codes = useCallback(() => {
    return damageCodesStorage.c2CodesAreLoading ? [] : damageCodesStorage.c2Codes;
  }, [damageCodesStorage.c2Codes, damageCodesStorage.c2CodesAreLoading]);

  useEffect(() => {
    async function getC2Codes() {
      try {
        await damagePropertiesStorageModel.getDamageC2CodesForMainAndC1Code(
          damageCodesStorage.selectedMainCode?.code ?? "",
          damageCodesStorage.selectedC1Code.code
        );
        damageCodesStorage.receivedC2Codes(damagePropertiesStorageModel.c2Codes);
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.c2Codes");
        handleErrorInSnackbar(applicationError);
      }
    }
    if (damageCodesStorage.selectedC1Code.hasC2Code && !damageCodesStorage.c2Codes.length) {
      getC2Codes();
    }
  }, [
    damageCodesStorage,
    damageCodesStorage.selectedMainCode,
    damageCodesStorage.selectedC1Code,
    damageCodesStorage.c2Codes,
    damageCodesStorage.c2CodesAreLoading,
    damagePropertiesStorageModel,
    handleErrorInSnackbar,
  ]);

  useEffect(() => {
    if (
      damageCodesStorage.newDamageCharacteristicsRequired &&
      !damageCodesStorage.loadingDamageCharacteristics
    ) {
      getDamageDetails();
    }
  }, [
    damageCodesStorage.loadingDamageCharacteristics,
    damageCodesStorage.newDamageCharacteristicsRequired,
    getDamageDetails,
  ]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
        {
          entity: EntityTypeFromPath.Damage,
          name: currentDamageStorage.currentDamage.id.toString(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentDamageStorage.currentDamage.id,
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditDamage, EditDamageFromAssignedProject]}
      titleKey="EditDamage.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
        damageId: currentDamageStorage.currentDamage.id,
      }}
      id="edit-damage"
      menuItems={menuItems}
      isLoading={isPageLoading()}
    >
      <Form
        formikOptions={{
          initialValues: getInitialFormValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: false,
        }}
        warningKey={damageIsLockedWarning?.key}
        warningContext={damageIsLockedWarning?.context}
        canSubmit={currentDamageStorage.canSubmitEdit}
        onCancel={goToDamagePage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.damage.details">
                <EditDamageImage
                  image={imageUploadState}
                  initialImageUri={currentDamageStorage.imageUrl}
                />
                <SubSection>
                  <EditGroupCode
                    value={damageCodesStorage.selectedGroupCode}
                    groupCodes={damageCodesStorage.groupCodes}
                    onChange={(newGroupCode: DamageGroupCode) => {
                      damageCodesStorage.groupCodeSelected(newGroupCode);
                    }}
                    setFieldValue={setFieldValue}
                  />
                  <EditMainCode
                    labelKey={damageCodesStorage.mainCodeLabel}
                    value={damageCodesStorage.selectedMainCode}
                    mainCodes={damageCodesStorage.mainCodes}
                    onChange={(newMainCode: DamageMainCode | null) => {
                      damageCodesStorage.mainCodeSelected(newMainCode);
                    }}
                    setFieldValue={setFieldValue}
                  />
                  <EditC1Code
                    labelKey={damageCodesStorage.c1CodeLabel}
                    value={damageCodesStorage.selectedC1Code}
                    c1Codes={damageCodesStorage.c1Codes}
                    disabled={
                      !damageCodesStorage.c1Codes.length ||
                      !damageCodesStorage.selectedMainCode?.hasC1Codes
                    }
                    onChange={(newC1Code: DamageC1Code) => {
                      damageCodesStorage.c1CodeSelected(newC1Code);
                    }}
                    setFieldValue={setFieldValue}
                  />
                  <EditC2Code
                    labelKey={damageCodesStorage.c2CodeLabel}
                    value={damageCodesStorage.selectedC2Code}
                    c2Codes={getC2Codes()}
                    disabled={
                      !damageCodesStorage.selectedC1Code.hasC2Code ||
                      !damageCodesStorage.selectedC1Code.isValid ||
                      damageCodesStorage.c2CodesAreLoading
                    }
                    onChange={(newC2Code: DamageC2Code) => {
                      damageCodesStorage.c2CodeSelected(newC2Code);
                    }}
                    setFieldValue={setFieldValue}
                  />
                </SubSection>
                <SubSection hideRightBorder>
                  <DisplayDateCreated date={currentDamageStorage.currentDamage.created} />
                  <DisplayCreatedBy createdBy={currentDamageStorage.currentDamage.createdBy.name} />
                </SubSection>
              </DataSection>
              <EditDynamicProperties
                labelKey="general.characteristics"
                properties={damageCodesStorage.damageCharacteristicsProperties}
                values={values.dynamicProperties}
                nestedFieldsPath="dynamicProperties"
                setFieldValue={setFieldValue}
                allFieldsDisabled={damageCodesStorage.characteristicsAreDisabled}
                loadingOverlay={isLoadingDamageCharacteristics()}
              />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});

export default EditDamagePage;
